import {
  type PropsWithChildren,
  type CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useResizeObserver from "use-resize-observer";
import useTimeout from "@rooks/use-timeout";
import { mergeRefs } from "react-merge-refs";
import { cx } from "cva";

type DomEl = null | HTMLDivElement;

export type CollapsibleProps = PropsWithChildren<{
  className?: string;
  innerClassName?: string;
  expanded: boolean;
  duration?: number;
  animationType?: "scale" | "translate";
}>;

const Collapsible = forwardRef<HTMLDivElement, CollapsibleProps>(
  (
    { children, className, innerClassName, expanded, duration, animationType = "translate" },
    ref,
  ) => {
    const innerEl = useRef<DomEl>(null);
    const wrapperEl = useRef<DomEl>(null);
    const [height, setHeight] = useState<number | null>(null);

    const durationStyle = useMemo<CSSProperties | undefined>(() => {
      if (typeof duration !== "number") return;
      return { transitionDuration: `${duration}ms` };
    }, [duration]);

    const { start, clear } = useTimeout(() => {
      measure(innerEl.current);
      updateHeight();
    }, 500);

    useResizeObserver<HTMLDivElement>({
      ref: innerEl,
      onResize: () => {
        if (
          !wrapperEl.current ||
          !innerEl.current ||
          !wrapperEl.current.contains(innerEl.current)
        ) {
          return;
        }
        clear();
        start();
      },
    });

    useEffect(() => {
      updateHeight();
    }, [expanded, height]);

    const measure = useCallback((el: DomEl) => {
      if (!el) return;
      setHeight(el.offsetHeight);
    }, []);

    const updateInnerHeight = useCallback((heightParam: number | null) => {
      if (!wrapperEl.current || heightParam === null) return;

      if (wrapperEl.current.style.height !== heightParam + "px") {
        wrapperEl.current.style.height = heightParam + "px";
      }
      if (!height || !innerEl.current) return;

      if (animationType === "scale") {
        innerEl.current.style.transform = `scaleY(${heightParam / height})`;
      } else {
        innerEl.current.style.transform = `translate3d(0,${heightParam - height}px,0)`;
      }
    }, []);

    const updateHeight = useCallback(() => {
      if (!innerEl.current || !wrapperEl.current) return;

      const executeHeightUpdate = () => {
        if (wrapperEl.current && innerEl.current && wrapperEl.current.contains(innerEl.current)) {
          measure(innerEl.current);
          updateInnerHeight(height);
        } else {
          requestAnimationFrame(executeHeightUpdate);
        }
      };

      if (expanded) {
        if (!wrapperEl.current.contains(innerEl.current)) {
          wrapperEl.current.appendChild(innerEl.current);
          executeHeightUpdate();
        } else {
          measure(innerEl.current);
          updateInnerHeight(height);
        }
      } else {
        updateInnerHeight(0);
      }
    }, [expanded, height, measure, updateInnerHeight]);

    const transitionEnd = () => {
      if (expanded) return;
      if (innerEl.current && wrapperEl.current?.contains(innerEl.current)) {
        wrapperEl.current.removeChild(innerEl.current);
      }
    };

    return (
      <div
        ref={mergeRefs([wrapperEl, ref])}
        className={cx("kn-collapsible", className)}
        style={{ ...durationStyle }}
        onTransitionEnd={transitionEnd}
      >
        <div
          ref={innerEl}
          className={cx("kn-collapsible-inner", innerClassName)}
          style={{ ...durationStyle }}
        >
          {children}
        </div>
      </div>
    );
  },
);

Collapsible.displayName = "Collapsible";

export default Collapsible;
